import PermissionContext from 'contexts/PermissionContext';
import { useContext } from 'react';

type IUsePermission = {
  type: ('list' | 'read' | 'write')[] | 'list' | 'read' | 'write'
  action: string | string[];
};

export const usePermission = () => {
  const { permissions, updateAssetClassPermission, serviceName } = useContext(PermissionContext);

    const filterActionsByType = (type: IUsePermission['type'], data: any): string[] => {
    if (typeof type === 'string') {
      // single type
      return data[type] || []
    } else {
      // multiple types
      return type.flatMap((t) => data[t] || [])
    }
  }

  const checkModuleAccess = (assetId?: string | number): boolean => {
    // to check if user has module access by looking for 'GetModuleAccess' in their permissions
    // If assetId is provided, check subproject permissions; otherwise, check project permissions
    const readActions = assetId ? permissions?.subprojects?.[assetId]?.read || [] : permissions?.project?.read || []
    return readActions.some((act) => act === 'GetModuleAccess' || act.endsWith(':GetModuleAccess'))
  }

  const checkProjectPermission = (props: IUsePermission): boolean | null => {
    const { action, type } = props;
    if (!permissions || !permissions.project) return null;
    const actions = Array.isArray(action) ? action : [action];

    if (checkModuleAccess() === false) {
      return false
    }

    if (!serviceName) {
      return actions.some((act) => filterActionsByType(type, permissions.project).includes(`${act}`));
    }

    return actions.some((act) => filterActionsByType(type, permissions.project).includes(`${String(serviceName)}:${act}`));
  };

  const checkSubprojectPermission = (props: IUsePermission & { assetId: string | number }): boolean | null => {
    const { action, type, assetId } = props;
    if (!permissions || !permissions.subprojects || !permissions.subprojects[assetId]) return null;
    const actions = Array.isArray(action) ? action : [action];

    if (checkModuleAccess(assetId) === false || checkModuleAccess() === false) {
      return false
    }
    // Handle modules without service names
    if (!serviceName) {
      return actions.some((act) => filterActionsByType(type, permissions.subprojects[assetId]).includes(`${act}`));
    }

    return actions.some((act) => filterActionsByType(type, permissions.subprojects[assetId]).includes(`${String(serviceName)}:${act}`));
    
  };

  const checkAllSubprojectsPermission = ({ action, type }: IUsePermission): boolean | null => {
    if (!permissions || !permissions.subprojects || Object.keys(permissions.subprojects).length === 0) return null;
    if (checkModuleAccess() === false) {
      return false
    }
    return Object.keys(permissions.subprojects).some((assetId) =>
      checkSubprojectPermission({ action, type, assetId })
    );
  };

  const checkAllSubprojectsFormsPermission = ({ action, type }: IUsePermission): boolean | null => {
    if (!permissions || !permissions.subprojects || Object.keys(permissions.subprojects).length === 0) return null;
    if (checkModuleAccess() === false) {
      return false
    }

    return Object.keys(permissions.subprojects).every((assetId) =>
      checkSubprojectPermission({ action, type, assetId })
    );
  };

  return {
    checkProjectPermission,
    checkSubprojectPermission,
    checkAllSubprojectsPermission,
    permissions,
    updateAssetClassPermission,
    checkAllSubprojectsFormsPermission,
  };
};
